import { MouseEvent, useMemo, useState } from "react";
import { Accordion, Badge, Button, Center, Divider, Group, Loader, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCheck } from "@tabler/icons-react";

import { getAge, getGender, getParticipant, getParticipantName, getParticipantNameReversed } from "../../functions/programregistration.functions";
import { ChildParticipant } from "../../models/programregistration.models";
import { ProgramLeaveType } from "../../models/programs.models";
import { ProgramActivitySessionEnrollmentRegistration, useProgramActivitySessionEnrollmentCheckInMutation, useProgramActivitySessionEnrollmentCheckOutMutation } from "../../api/programactivitysession.api";
import ParentGuardianContactModal from "./ParentGuardianContactModal";
import classes from "./ProgramSessionCheckIn.module.css";

interface ProgramActivitySessionCheckInTableProps {
	enrollments: ProgramActivitySessionEnrollmentRegistration[];
	isLoading: boolean;
	programActivitySessionId: number;
}

interface ProgramActivitySessionCheckInParentGuardian {
	parentGuardianId: string;
	name: string;
	location: string;
	mobilePhone: string;
	emergencyContact: string;
}

interface ProgramActivitySessionCheckInRow {
	id: string;
	registrationId: number;
	enrollmentId: number;
	checkedIn: boolean;
	checkedOut: boolean;
	participantName: string;
	subtitle: string;
	needsSupport: string;
	healthConcerns: string;
	allergies: string;
	specialNeeds: string;
	parentGuardians: ProgramActivitySessionCheckInParentGuardian[];
}

const ProgramActivitySessionCheckIn = ({ enrollments, isLoading, programActivitySessionId }: ProgramActivitySessionCheckInTableProps) => {
	const [openItems, setOpenItems] = useState<string[]>([]);
	const [selectedParentGuardian, setSelectedParentGuardian] = useState<{
		registrationId: number;
		parentGuardianId: string;
		name: string;
		location: string;
		mobilePhone: string;
		emergencyContact: string;
	} | null>(null);
	const checkInMutation = useProgramActivitySessionEnrollmentCheckInMutation(programActivitySessionId);
	const checkOutMutation = useProgramActivitySessionEnrollmentCheckOutMutation(programActivitySessionId);

	const toggleCheckIn = (programActivitySessionEnrollmentId: number, checkedIn: boolean) => {
		checkInMutation.mutate({ programActivitySessionEnrollmentId, checkedIn });
	}

	const toggleCheckInWithCanLeave = (programActivitySessionEnrollmentId: number, checkedIn: boolean) => {
		checkInMutation.mutate({ programActivitySessionEnrollmentId, checkedIn });
	}

	const toggleCheckOut = (programActivitySessionEnrollmentId: number, checkedOut: boolean) => {
		checkOutMutation.mutate({ programActivitySessionEnrollmentId, checkedOut });
	}

	const onCheckInClick = (event: MouseEvent, row: { id: string, enrollmentId: number, checkedIn: boolean, participantName: string }) => {
		event.stopPropagation();

		if (!row.checkedIn) {
			toggleCheckIn(row.enrollmentId, true);
			setOpenItems((prev) => prev.filter((item) => item !== row.id));
			return;
		}

		modals.openConfirmModal({
			title: <Text size="lg" fw="bold">Undo Check In</Text>,
			children: <Text size="sm">Undo check in for {row.participantName || "this participant"}?</Text>,
			labels: { confirm: "Undo Check In", cancel: "Cancel" },
			confirmProps: { color: "red" },
			cancelProps: { variant: "subtle" },
			onConfirm: () => {
				toggleCheckIn(row.enrollmentId, false);
				setOpenItems((prev) => prev.filter((item) => item !== row.id));
			},
		});
	}

	const onUpdateContactClick = (event: MouseEvent, row: ProgramActivitySessionCheckInRow, parentGuardian: ProgramActivitySessionCheckInParentGuardian) => {
		event.stopPropagation();

		if (!parentGuardian.parentGuardianId) {
			return;
		}

		setSelectedParentGuardian({
			registrationId: row.registrationId,
			parentGuardianId: parentGuardian.parentGuardianId,
			name: parentGuardian.name,
			location: parentGuardian.location,
			mobilePhone: parentGuardian.mobilePhone,
			emergencyContact: parentGuardian.emergencyContact,
		});
	}

	const onCheckOutClick = (event: MouseEvent, row: { id: string, enrollmentId: number, checkedOut: boolean, participantName: string }) => {
		event.stopPropagation();

		if (!row.checkedOut) {
			toggleCheckOut(row.enrollmentId, true);
			setOpenItems((prev) => prev.filter((item) => item !== row.id));
			return;
		}

		modals.openConfirmModal({
			title: <Text size="lg" fw="bold">Undo Check Out</Text>,
			children: <Text size="sm">Undo check out for {row.participantName || "this participant"}?</Text>,
			labels: { confirm: "Undo Check Out", cancel: "Cancel" },
			confirmProps: { color: "red" },
			cancelProps: { variant: "subtle" },
			onConfirm: () => {
				toggleCheckOut(row.enrollmentId, false);
				setOpenItems((prev) => prev.filter((item) => item !== row.id));
			},
		});
	}

	const displayValue = (value?: string | null) => {
		if (!value || value.trim() === "") {
			return "N/A";
		}

		return value;
	}

	const rows = useMemo(() => {
		return enrollments
			.map((enrollment) => {
				const participant = getParticipant(enrollment.Registration, enrollment.ProgramActivitySessionEnrollment.ParticipantId);
				const participantName = getParticipantNameReversed(enrollment.Registration, enrollment.ProgramActivitySessionEnrollment.ParticipantId);
				const age = getAge(participant as ChildParticipant, enrollment.Registration.DateFrom) ?? "Adult";
				const gender = participant ? getGender(participant) : "Unknown";
				const childParticipant = participant as ChildParticipant | undefined;
				const parentGuardians = enrollment.Registration.Data.ParentGuardians ?? [];

				return {
					id: `${enrollment.Registration.Id}-${enrollment.ProgramActivitySessionEnrollment.ParticipantId}`,
					registrationId: enrollment.Registration.Id,
					enrollmentId: enrollment.ProgramActivitySessionEnrollment.Id,
					checkedIn: enrollment.ProgramActivitySessionEnrollment.CheckedIn === true,				
					checkedOut: enrollment.ProgramActivitySessionEnrollment.CheckedOut === true,					
					participantName,
					subtitle: `Age ${age}, ${gender}`,
					needsSupport: childParticipant?.NeedsSupport === true ? "Yes" : "No",
					healthConcerns: displayValue(childParticipant?.HealthConcerns),
					allergies: displayValue(childParticipant?.Allergies ?? participant?.Allergies),
					specialNeeds: displayValue(childParticipant?.SpecialNeeds),
					parentGuardians: parentGuardians.map((parentGuardian) => ({
						parentGuardianId: parentGuardian.Id,
						name: `${parentGuardian.FirstName ?? ""} ${parentGuardian.LastName ?? ""}`.trim() || "(unavailable)",
						location: parentGuardian.Location ?? "",
						mobilePhone: parentGuardian.MobilePhone ?? "",
						emergencyContact: parentGuardian.EmergencyContact ?? "",
					})),
				} as ProgramActivitySessionCheckInRow;
			})
			.sort((left, right) => {
				const statusOrder = (row: { checkedIn: boolean, checkedOut: boolean }) => {
					if (!row.checkedIn) return 0;
					if (!row.checkedOut) return 1;
					return 2;
				};
				const statusDiff = statusOrder(left) - statusOrder(right);
				if (statusDiff !== 0) return statusDiff;
				return left.participantName.localeCompare(right.participantName);
			});
	}, [enrollments]);

	if (isLoading) {
		return <Center h={150} mx="auto">
			<Loader />
		</Center>;
	}

	if (rows.length === 0) {
		return <Paper p="md">
			<Text c="dimmed">No participants found for this session.</Text>
		</Paper>;
	}

	return <Accordion multiple radius="md" variant="default" chevronPosition="left" classNames={{ chevron: classes.chevron, control: classes.control }} value={openItems} onChange={setOpenItems}>
		{rows.map((row) => {
			return <Accordion.Item value={row.id} key={row.id}>
				<Accordion.Control component="div">
					<Group justify="space-between" align="flex-start" gap={0} wrap="nowrap" w="100%">
						<Stack gap={0}>
							<Text fw={600}>{row.participantName || "(unavailable)"}</Text>
							<Group gap={4} align="center">
								<Text size="sm" c="dimmed">{row.subtitle}</Text>
							</Group>
						</Stack>
						<Group gap="xs" wrap="wrap" align="flex-start" justify="flex-end">
							{!row.checkedOut && (
							<Button
								variant={row.checkedIn ? "light" : "filled"}
								color={row.checkedIn ? "green" : undefined}
								size="md"
								radius="lg"
								onClick={(event) => onCheckInClick(event, row)}
								loading={checkInMutation.isLoading}
								leftSection={row.checkedIn ? <IconCheck size={20} stroke={3} /> : null}
							>
								{row.checkedIn ? "Checked In" : "Check In"}
							</Button>
							)}
							{row.checkedIn && (
								<Button
									variant={row.checkedOut ? "light" : "filled"}
									color={row.checkedOut ? "green" : undefined}
									size="md"
									radius="lg"
									onClick={(event) => onCheckOutClick(event, row)}
									loading={checkOutMutation.isLoading}
									leftSection={row.checkedOut ? <IconCheck size={20} stroke={3} /> : null}
								>
									{row.checkedOut ? "Checked Out" : "Check Out"}
								</Button>
							)}
						</Group>
					</Group>
				</Accordion.Control>
				<Accordion.Panel>
					<Stack gap={8} mt="xs">
						
						{/* <Divider /> */}
						<SimpleGrid cols={{ xs: 2, md: 3 }} spacing="md">
							<Paper bg="var(--mantine-color-default-hover)" withBorder p="md" radius="md">
								<Stack gap={2} mb="xs">
									<Text size="sm" fw={600}>Participant Details</Text>
									{row.needsSupport === "Yes" && <Badge color="green" variant="filled" radius="sm">One-on-One Support Required</Badge>}
									<Text size="sm">Health concerns: {row.healthConcerns}</Text>
									<Text size="sm">Allergies: {row.allergies}</Text>
									<Text size="sm">Special needs: {row.specialNeeds}</Text>
								</Stack>
								<Button
									variant="light"
									component="a"
									href={`/registrations/view/${row.registrationId}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									View Registration
								</Button>
							</Paper>
							{row.parentGuardians.length === 0 && <>
								<Paper bg="var(--mantine-color-default-hover)" withBorder p="md" radius="md">
									<Stack gap={2}>
										<Text size="sm" fw={600}>Parent / Guardian</Text>
										<Text size="sm">No parent / guardian information available.</Text>
									</Stack>
								</Paper>
							</>}
							{row.parentGuardians.map((parentGuardian, index) => {
								return <Paper key={`${row.id}-guardian-${index}`} bg="var(--mantine-color-default-hover)" withBorder p="md" radius="md">
									<Stack gap={2} mb="xs">
										<Text size="sm" fw={600}>Parent / Guardian</Text>
										<Stack key={`${row.id}-guardian-${index}`} gap={0}>
											<Text size="sm">
												{parentGuardian.name}
											</Text>
											<Text size="sm">Location: {displayValue(parentGuardian.location)}</Text>
											<Text size="sm">Cell Phone: {displayValue(parentGuardian.mobilePhone)}</Text>
											<Text size="sm">Emergency Contact: {displayValue(parentGuardian.emergencyContact)}</Text>
										</Stack>
									</Stack>
									<Button variant="light" onClick={(event) => onUpdateContactClick(event, row, parentGuardian)} disabled={!parentGuardian.parentGuardianId}>Update Contact</Button>
								</Paper>
							})}
						</SimpleGrid>
					</Stack>
				</Accordion.Panel>
			</Accordion.Item>;
		})}

		<ParentGuardianContactModal
			opened={selectedParentGuardian != null}
			onClose={() => setSelectedParentGuardian(null)}
			programSessionId={programActivitySessionId}
			registrationId={selectedParentGuardian?.registrationId}
			parentGuardianId={selectedParentGuardian?.parentGuardianId}
			parentGuardianName={selectedParentGuardian?.name}
			location={selectedParentGuardian?.location}
			mobilePhone={selectedParentGuardian?.mobilePhone}
			emergencyContact={selectedParentGuardian?.emergencyContact}
		/>
	</Accordion>;
}

export default ProgramActivitySessionCheckIn;